// Recharts donut for "Investment by Portfolio" on /executive and /financials.

import { PieChart, Pie, Cell } from 'recharts';
import './charts.css';
import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';
import { useChartContainerWidth } from './useChartContainerWidth.js';
import { donutCenterFontSizes, donutHolePx } from './donutCenterSizing.js';

const PALETTE = [
  '#00152c',
  '#002a4e',
  '#1961a1',
  '#81b9ff',
  '#5a8c66',
  '#c08828',
  '#c0524d',
  '#5a5e66',
];

export function InvestmentDonut({
  data = [],
  height = 240,
  innerRadius = '62%',
  outerRadius = '96%',
  centerValue,
  centerLabel,
  ariaLabel = 'Investment by portfolio',
}) {
  const tooltip = useOptionalRichTooltipController();
  const { ref: chartRef, width: chartWidth } = useChartContainerWidth();
  const total = data.reduce((acc, d) => acc + (d.value || 0), 0);
  // Size the center text to the MEASURED donut hole — a fixed headline token
  // overflows the ring on narrow cards (home span-4 squeezes this to ~130px).
  const holePx = chartWidth > 0 ? donutHolePx(chartWidth, height, innerRadius) : null;
  const centerFonts = holePx ? donutCenterFontSizes(holePx, centerValue ?? '') : null;
  return (
    <div className="donut" role="group" aria-label={ariaLabel}>
      <div className="donut-wrap" style={{ height }} ref={chartRef}>
        {chartWidth > 0 ? (
          <PieChart width={chartWidth} height={height}>
            <Pie
              data={data}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={2}
              stroke="rgba(255,255,255,0.85)"
              strokeWidth={2}
            >
              {data.map((entry, i) => {
                const key = entry.id || entry.name;
                const triggerProps =
                  entry.richTooltip && tooltip
                    ? tooltip.getTriggerProps({
                        surfaceId: entry.richTooltip.surfaceId,
                        payload: entry.richTooltip,
                        testIdPrefix: 'rich-tooltip',
                        ariaLabel: `${entry.name} ${Math.round(entry.value || 0)} planned`,
                      })
                    : {};
                const { ref: _sliceRef, ...sliceTriggerProps } = triggerProps;
                return (
                  <Cell
                    key={key}
                    fill={PALETTE[i % PALETTE.length]}
                    data-testid={`investment-donut-slice-${key}`}
                    className="rich-tooltip-svg-trigger donut-slice-trigger"
                    tabIndex={entry.richTooltip ? 0 : undefined}
                    role={entry.richTooltip ? 'button' : undefined}
                    {...sliceTriggerProps}
                  />
                );
              })}
            </Pie>
          </PieChart>
        ) : null}
        <div className="donut-center">
          <span
            className="donut-center-value"
            style={centerFonts ? { fontSize: `${centerFonts.valuePx}px` } : undefined}
          >
            {centerValue ?? ''}
          </span>
          <span
            className="donut-center-label"
            style={centerFonts ? { fontSize: `${centerFonts.labelPx}px` } : undefined}
          >
            {centerLabel ?? ''}
          </span>
        </div>
      </div>
      <ul className="donut-legend">
        {data.map((d, i) => {
          const pct = total ? Math.round((d.value / total) * 100) : 0;
          const key = d.id || d.name;
          const triggerProps =
            d.richTooltip && tooltip
              ? tooltip.getTriggerProps({
                  surfaceId: d.richTooltip.surfaceId,
                  payload: d.richTooltip,
                  testIdPrefix: 'rich-tooltip',
                  ariaLabel: `${d.name} ${pct}% of planned spend`,
                })
              : {};
          return (
            <li key={d.name}>
              <span className="donut-swatch" style={{ background: PALETTE[i % PALETTE.length] }} />
              {d.richTooltip ? (
                <button
                  type="button"
                  className="donut-legend-trigger rich-tooltip-trigger-surface"
                  data-testid={`investment-donut-legend-${key}`}
                  {...triggerProps}
                >
                  <span className="donut-legend-name">{d.name}</span>
                  <span className="donut-legend-pct">{pct}%</span>
                </button>
              ) : (
                <>
                  <span className="donut-legend-name">{d.name}</span>
                  <span className="donut-legend-pct">{pct}%</span>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
